import { useState } from 'react';
import { motion } from 'framer-motion';
import {
  Phone,
  Mail,
  MapPin,
  Clock,
  Send,
  CheckCircle2 } from
'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue } from
'../components/ui/select';
import { toast } from 'sonner';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true },
  transition: { duration: 0.5, ease: 'easeOut' }
};

const services = [
'Irrigation Installation',
'System Blueprints',
'Yard Repair',
'Landscape Lighting',
'Invisible Dog Fencing',
'Back Flow Testing',
'Seasonal Maintenance',
'Other'];


const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleServiceChange = (value) => {
    setFormData((prev) => ({ ...prev, service: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      await axios.post(`${API}/contact`, formData);
      setIsSubmitted(true);
      toast.success('Message sent successfully! We\'ll get back to you soon.');
      setFormData({ name: '', email: '', phone: '', service: '', message: '' });
    } catch (error) {
      console.error('Error submitting form:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="pt-24 md:pt-28" data-testid="contact-page">
      {/* Hero Section */}
      <section className="relative py-24 md:py-32 bg-mist-dark" data-testid="contact-hero">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center">

            <span className="inline-block px-4 py-2 bg-landscape-green/20 text-landscape-green font-body text-sm font-bold uppercase tracking-widest rounded-full mb-6">
              Contact Us
            </span>
            <h1 className="font-heading text-5xl md:text-6xl font-bold text-mist-white mb-6">
              Get in <span className="text-mist-light">Touch</span>
            </h1>
            <p className="font-body text-lg md:text-xl text-mist-light/80 max-w-2xl mx-auto">
              Ready to start your project? Have questions? We'd love to hear from you. 
              Reach out and let's discuss how we can help.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Contact Form & Info */}
      <section className="py-24 md:py-32 bg-mist-white" data-testid="contact-form-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Form */}
            <motion.div {...fadeUp}>
              <Card className="bg-white border-0 shadow-xl rounded-2xl overflow-hidden">
                <CardContent className="p-8 md:p-10">
                  <h2 className="font-heading text-3xl font-bold text-mist-dark mb-2">
                    Send Us a Message
                  </h2>
                  <p className="font-body text-mist-blue mb-8">
                    Fill out the form below and we'll get back to you within 24 hours.
                  </p>

                  {isSubmitted ?
                  <div className="text-center py-12" data-testid="form-success">
                      <div className="w-20 h-20 bg-landscape-green/10 rounded-full flex items-center justify-center mx-auto mb-6">
                        <CheckCircle2 className="w-10 h-10 text-landscape-green" />
                      </div>
                      <h3 className="font-heading text-2xl font-bold text-mist-dark mb-4">
                        Message Sent!
                      </h3>
                      <p className="font-body text-mist-blue mb-6">
                        Thank you for reaching out. We'll get back to you soon.
                      </p>
                      <Button
                      onClick={() => setIsSubmitted(false)}
                      className="bg-mist-blue hover:bg-mist-dark text-white rounded-full px-6"
                      data-testid="send-another-btn">

                        Send Another Message
                      </Button>
                    </div> :

                  <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="name" className="font-body text-mist-dark font-medium">
                            Full Name *
                          </Label>
                          <Input
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          placeholder="John Doe"
                          required
                          className="border-mist-light focus:border-mist-blue rounded-xl h-12"
                          data-testid="input-name" />

                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="email" className="font-body text-mist-dark font-medium">
                            Email Address *
                          </Label>
                          <Input
                          id="email"
                          name="email"
                          type="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          placeholder="john@example.com"
                          required
                          className="border-mist-light focus:border-mist-blue rounded-xl h-12"
                          data-testid="input-email" />

                        </div>
                      </div>

                      <div className="grid md:grid-cols-2 gap-6">
                        <div className="space-y-2">
                          <Label htmlFor="phone" className="font-body text-mist-dark font-medium">
                            Phone Number
                          </Label>
                          <Input
                          id="phone"
                          name="phone"
                          type="tel"
                          value={formData.phone}
                          onChange={handleInputChange}
                          placeholder="(555) 123-4567"
                          className="border-mist-light focus:border-mist-blue rounded-xl h-12"
                          data-testid="input-phone" />

                        </div>
                        <div className="space-y-2">
                          <Label htmlFor="service" className="font-body text-mist-dark font-medium">
                            Service Interested In
                          </Label>
                          <Select onValueChange={handleServiceChange} value={formData.service}>
                            <SelectTrigger className="border-mist-light focus:border-mist-blue rounded-xl h-12" data-testid="select-service">
                              <SelectValue placeholder="Select a service" />
                            </SelectTrigger>
                            <SelectContent>
                              {services.map((service) =>
                            <SelectItem key={service} value={service}>
                                  {service}
                                </SelectItem>
                            )}
                            </SelectContent>
                          </Select>
                        </div>
                      </div>

                      <div className="space-y-2">
                        <Label htmlFor="message" className="font-body text-mist-dark font-medium">
                          Your Message *
                        </Label>
                        <Textarea
                        id="message"
                        name="message"
                        value={formData.message}
                        onChange={handleInputChange}
                        placeholder="Tell us about your project or questions..."
                        required
                        rows={5}
                        className="border-mist-light focus:border-mist-blue rounded-xl resize-none"
                        data-testid="input-message" />

                      </div>

                      <Button
                      type="submit"
                      disabled={isSubmitting}
                      className="w-full bg-landscape-green hover:bg-landscape-dark text-white rounded-full h-14 text-lg font-medium"
                      data-testid="submit-button">

                        {isSubmitting ?
                      <span className="flex items-center gap-2">
                            <svg className="animate-spin w-5 h-5" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Sending...
                          </span> :

                      <span className="flex items-center gap-2">
                            <Send className="w-5 h-5" />
                            Send Message
                          </span>
                      }
                      </Button>
                    </form>
                  }
                </CardContent>
              </Card>
            </motion.div>

            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-8">

              <div>
                <h2 className="font-heading text-3xl font-bold text-mist-dark mb-2">
                  Contact Information
                </h2>
                <p className="font-body text-mist-blue">
                  Reach out directly or visit our office. We're here to help!
                </p>
              </div>

              <div className="space-y-6">
                <Card className="bg-white border-0 shadow-lg rounded-2xl" data-testid="contact-phone">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-mist-light rounded-xl flex items-center justify-center flex-shrink-0">
                      <Phone className="w-6 h-6 text-mist-blue" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-mist-dark mb-1">
                        Phone
                      </h3>
                      <p className="font-body text-mist-blue">(724) 294-5999</p>
                      <p className="font-body text-sm text-mist-blue/70">Mon-Fri 8am-6pm</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-0 shadow-lg rounded-2xl" data-testid="contact-email">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-mist-light rounded-xl flex items-center justify-center flex-shrink-0">
                      <Mail className="w-6 h-6 text-mist-blue" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-mist-dark mb-1">
                        Email
                      </h3>
                      <p className="font-body text-mist-blue">info@mistirrigationllc.com</p>
                      <p className="font-body text-sm text-mist-blue/70">We respond within 24 hours</p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-0 shadow-lg rounded-2xl" data-testid="contact-address">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-mist-light rounded-xl flex items-center justify-center flex-shrink-0">
                      <MapPin className="w-6 h-6 text-mist-blue" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-mist-dark mb-1">
                        Address
                      </h3>
                      <p className="font-body text-mist-blue">115 Petry Lane
                        <br />Zelionople, PA 16063

                      </p>
                    </div>
                  </CardContent>
                </Card>

                <Card className="bg-white border-0 shadow-lg rounded-2xl" data-testid="contact-hours">
                  <CardContent className="p-6 flex items-start gap-4">
                    <div className="w-12 h-12 bg-mist-light rounded-xl flex items-center justify-center flex-shrink-0">
                      <Clock className="w-6 h-6 text-mist-blue" />
                    </div>
                    <div>
                      <h3 className="font-heading text-lg font-bold text-mist-dark mb-1">
                        Business Hours
                      </h3>
                      <p className="font-body text-mist-blue">
                        Monday - Friday: 8:00 AM - 6:00 PM<br />
                        Saturday: 9:00 AM - 2:00 PM<br />
                        Sunday: Closed
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </div>

              {/* Emergency Service */}
              <Card className="bg-landscape-green border-0 shadow-lg rounded-2xl" data-testid="emergency-service">
                <CardContent className="p-6">
                  <h3 className="font-heading text-xl font-bold text-white mb-2">
                    24/7 Emergency Service
                  </h3>
                  <p className="font-body text-white/90 mb-4">
                    Have an irrigation emergency? We're available around the clock for urgent repairs.
                  </p>
                  <a href="tel:7242945999">
                    <Button
                      className="bg-white text-landscape-green hover:bg-mist-white rounded-full px-6"
                      data-testid="emergency-call-btn">

                      <Phone className="w-4 h-4 mr-2" />
                      Call Emergency Line
                    </Button>
                  </a>
                </CardContent>
              </Card>
            </motion.div>
          </div>
        </div>
      </section>
    </div>);

};

export default Contact;